import { useEffect, useState } from "react";
import { coverUrl } from "@/lib/api";

const cache = new Map();
export const FALLBACK = { r: 245, g: 158, b: 11 }; // amber

/* Dominant (saturation-weighted) colour of an image; cached per URL, resolves to FALLBACK when the image can't be read. */
export function dominantColor(url) {
  if (!url) return Promise.resolve(FALLBACK);
  if (cache.has(url)) return cache.get(url);
  const p = new Promise((resolve) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => {
      try {
        const n = 28, cv = document.createElement("canvas");
        cv.width = n; cv.height = n;
        const ctx = cv.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, n, n);
        const d = ctx.getImageData(0, 0, n, n).data;
        let r = 0, g = 0, b = 0, w = 0;
        for (let i = 0; i < d.length; i += 4) {
          const R = d[i], G = d[i + 1], B = d[i + 2];
          const max = Math.max(R, G, B), min = Math.min(R, G, B), l = (max + min) / 2;
          const sat = max === min ? 0 : (max - min) / (l > 127 ? 510 - max - min : max + min);
          const k = 0.08 + sat * sat * (1 - Math.abs(l - 128) / 160);
          r += R * k; g += G * k; b += B * k; w += k;
        }
        if (!w) return resolve(FALLBACK);
        let c = { r: r / w, g: g / w, b: b / w };
        const max = Math.max(c.r, c.g, c.b);
        if (max < 90) { const f = 90 / Math.max(max, 1); c = { r: c.r * f, g: c.g * f, b: c.b * f }; } // lift very dark art so the glow shows
        resolve({ r: Math.round(c.r), g: Math.round(c.g), b: Math.round(c.b) });
      } catch { resolve(FALLBACK); }
    };
    img.onerror = () => resolve(FALLBACK);
    img.src = url;
  });
  cache.set(url, p);
  return p;
}

export const rgba = (c, a) => `rgba(${c.r},${c.g},${c.b},${a})`;
export const rgb = (c) => `rgb(${c.r},${c.g},${c.b})`;

/* Up to `n` distinct, saturation-weighted colours from an image (coarse 4-bit bucket quantisation). */
export function palette(url, n = 4) {
  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => {
      try {
        const s = 48, cv = document.createElement("canvas");
        cv.width = s; cv.height = s;
        const ctx = cv.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, s, s);
        const d = ctx.getImageData(0, 0, s, s).data, buckets = new Map();
        for (let i = 0; i < d.length; i += 4) {
          const R = d[i], G = d[i + 1], B = d[i + 2];
          const max = Math.max(R, G, B), min = Math.min(R, G, B), l = (max + min) / 2;
          const sat = max === min ? 0 : (max - min) / (l > 127 ? 510 - max - min : max + min);
          const k = (R >> 4) << 8 | (G >> 4) << 4 | (B >> 4), w = 0.15 + sat * (1 - Math.abs(l - 128) / 180);
          const b = buckets.get(k) || { r: 0, g: 0, b: 0, w: 0 };
          b.r += R * w; b.g += G * w; b.b += B * w; b.w += w; buckets.set(k, b);
        }
        const sorted = [...buckets.values()].sort((a, b) => b.w - a.w).map((b) => ({ r: b.r / b.w, g: b.g / b.w, b: b.b / b.w }));
        const out = [];
        for (const c of sorted) {
          if (out.every((o) => Math.hypot(o.r - c.r, o.g - c.g, o.b - c.b) > 60)) out.push(c);
          if (out.length >= n) break;
        }
        resolve(out.length ? out.map((c) => ({ r: Math.round(c.r), g: Math.round(c.g), b: Math.round(c.b) })) : [FALLBACK]);
      } catch { resolve([FALLBACK]); }
    };
    img.onerror = () => resolve([FALLBACK]);
    img.src = url;
  });
}

/* Dominant colour of an album's cover (FALLBACK while loading / for radio or no cover). */
export function useArtTint(albumId) {
  const [tint, setTint] = useState(FALLBACK);
  useEffect(() => {
    if (!albumId) { setTint(FALLBACK); return; }
    let alive = true;
    dominantColor(coverUrl(albumId)).then((c) => { if (alive) setTint(c); });
    return () => { alive = false; };
  }, [albumId]);
  return tint;
}
